import React from "react";
import { inject, observer } from "mobx-react";
import { Meeting, User } from "src/models";
import { MeetingStore, UserStore } from "src/stores";
import { UserSelect, Option, Checkbox, Text, Avatar, Spacer, Icon, IconId } from "src/components";
import { IMeeting, LoadingStatus, UserType } from "src/types";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import styled from "styled-components";
import { theme } from "src/config";
import { Item } from "framer-motion/types/components/Reorder/Item";
import { format } from "date-fns";

export interface IAttendanceViewProps {
  item:          Meeting;
  meetingStore?: MeetingStore;
  userStore?:    UserStore;
}

const tableHeader: {icon?:IconId; title: string; key: string;}[] = [
  {
    title: t("attendance"),
    key: "attendance",
    icon: undefined,
  },
  {
    title: t("name"),
    key: "name",
    icon: undefined,
  },
  {
    title: t("in"),
    key: "in",
    icon: "entry"
  },
  {
    title: t("out"),
    key: "out",
    icon: "logOut"
  },
]

export const AttendanceView = inject(
  "meetingStore",
  "userStore",
  "meetingStore"
)(
  observer((props: IAttendanceViewProps): JSX.Element => {
    const { item: meeting, userStore, meetingStore } = props;
    const { t, i18n } = useTranslation();

    const leader:       User   = meeting.getLeader;
    const participants: User[] = userStore.getLinks.filter(u => u.id !== leader?.id);
    const attendees:   {[user_id: number]: {in?: Date, out?: Date, checked_in: boolean}} = meetingStore.attendees || {};
    const isActive: boolean = meeting.id === meetingStore.getActiveMeeting?.id;

    return (
      <AttendanceListContainer>
        <table>
          <thead>
            <tr>
              {tableHeader.map((th)=>
              <th key={th.key}>
                <HeaderTitleContainer>
                <Icon size="medium" iconId={th.icon}/>
                <Spacer size="xxsmall"/>
                <Text color={theme.color.black} size="default" weight="bold">{th.title}</Text>
                </HeaderTitleContainer>
              </th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr key={leader.id}>
              <CheckboxData>
                <Checkbox
                  checked={attendees[leader.id]?.checked_in}
                  disabled
                />
              </CheckboxData>
              <UserData>
                <Avatar size="default" initials={leader.getInitials} image={leader.getAvatar}/>
                <Spacer size="xsmall"/>
                <Text size="small">{leader.displayName}</Text>
              </UserData>
              <td>{
                attendees[leader.id]?.in && 
                (!isActive || attendees[leader.id]?.in >= meeting.opened) && 
                format(attendees[leader.id]?.in,  "yyyy-MM-dd HH:mm:ss")
              }</td>
              <td>{
                attendees[leader.id]?.out &&
                (!isActive || attendees[leader.id]?.out >= meeting.opened) && 
                format(attendees[leader.id]?.out, "yyyy-MM-dd HH:mm:ss")
              }</td>
            </tr>
            {participants.map((user) => (
              <tr key={user.id}>
                <CheckboxData>
                  <Checkbox
                    checked  = {attendees[user.id]?.checked_in}
                    // disabled = {user.type !== UserType.Accountless}
                    onClick  = {() => meetingStore.checkInOut(user.id)}
                  />
                </CheckboxData>
                <UserData>
                  <Avatar size="default" initials={user.getInitials} image={user.getAvatar}/>
                  <Spacer size="xsmall"/>
                  <Text size="small">{user.displayName}</Text>
                </UserData>
                <td>{
                attendees[user.id]?.in && 
                (!isActive || attendees[user.id]?.in >= meeting.opened) && 
                format(attendees[user.id]?.in,  "yyyy-MM-dd HH:mm:ss")
              }</td>
              <td>{
                attendees[user.id]?.out &&
                (!isActive || attendees[user.id]?.out >= meeting.opened) && 
                format(attendees[user.id]?.out, "yyyy-MM-dd HH:mm:ss")
              }</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AttendanceListContainer>
    );
  })
);

const UserData = styled.td`
  display:        flex;
  flex-direction: row;
  align-items:    center;
`
const HeaderTitleContainer = styled.div`
  display:        flex;
  flex-direction: row;
  align-items:    center;
  padding-bottom: 0.5rem;
`

const CheckboxData = styled.td`
  text-align: center;
  vertical-align: middle;
`

const AttendanceListContainer = styled.div`
  margin: 0.5rem;

  table {
    width: 100%;
  }

  th {
    margin: 0 2rem;
    font-size:      ${theme.font.size.default};
    vertical-align: middle;
    border-bottom:  1px solid ${theme.color.slate3};
  }

  tr {
    margin-bottom: 2.75rem;
    align-items:   center;
  }

  td {
    font-size:   ${theme.font.size.small};
    padding-top: 2rem;

  }
`;